import { ShowEventsOptions } from '@nextforce/api'
import { Divider, Icon, makeStyles, Text, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import React, { useCallback } from 'react'
import {
  SectionList,
  SectionListData,
  SectionListRenderItemInfo,
  TextStyle,
  useWindowDimensions,
  View,
  ViewStyle,
} from 'react-native'
import {
  Menu,
  MenuOption,
  MenuOptions,
  MenuTrigger,
  renderers,
} from 'react-native-popup-menu'

export type DropdownItem = {
  name: string
  id: any
}

export interface DropdownItemComponentProps<
  Item extends DropdownItem = DropdownItem
> {
  item: Item
  selected: boolean
}

export type DropdownSection<Item extends DropdownItem = DropdownItem> =
  SectionListData<Item, { title?: string }>

export interface DropdownFooterOptionsProps {
  showWeeklySummaryOptionProps: ShowWeeklySummaryOptionProps
  showSummaryTilesOptionProps: ShowSummaryTilesOptionProps
  showEventsOptionProps: ShowEventsOptionProps
}

interface Props<Item extends DropdownItem> {
  sections: DropdownSection<Item>[]
  selectedId: any
  onSelect: (id: any) => void
  title?: string
  titleStyle?: TextStyle
  ItemComponent?: (props: DropdownItemComponentProps) => React.JSX.Element
  activeFilterNames?: () => string
  showCustomFilters?: boolean
  dropdownFooterOptionsProps?: DropdownFooterOptionsProps
  hideDownArrow?: boolean
  icon?: { name: string; type: string; solid?: boolean }
  buttonStyles?: ViewStyle
}

export function Dropdown<Item extends DropdownItem>({
  sections,
  selectedId,
  onSelect,
  title,
  titleStyle,
  ItemComponent,
  activeFilterNames,
  showCustomFilters,
  dropdownFooterOptionsProps,
  hideDownArrow,
  icon,
  buttonStyles,
}: Props<Item>) {
  const styles = useStyles()
  const dimensions = useWindowDimensions()
  const { theme } = useTheme()

  const selectedName = sections
    .flatMap(section => section.data)
    .find(item => item.id === selectedId)?.name

  const filterNames = activeFilterNames && activeFilterNames()

  const isScheduleSearchOption = useCallback(
    (item: DropdownItem) => {
      if (!showCustomFilters) {
        return false
      }
      const sectionsLength = sections[0].data.length

      return activeFilterNames && item.id >= sectionsLength
        ? item.id - sectionsLength === sectionsLength - 1
        : item.id === sectionsLength - 1
    },
    [sections, showCustomFilters, activeFilterNames]
  )

  return (
    <Menu
      renderer={renderers.Popover}
      rendererProps={{
        placement: 'bottom',
        anchorStyle: styles.anchorStyle,
      }}
    >
      <MenuTrigger>
        <View style={[styles.button, buttonStyles]}>
          {icon ? (
            <Icon
              type={icon.type}
              name={icon.name}
              solid={icon.solid}
              size={15}
              color={theme.colors?.primary}
              containerStyle={styles.icon}
            />
          ) : (
            <Text
              style={[styles.buttonText, titleStyle]}
              numberOfLines={activeFilterNames ? 1 : undefined}
            >
              {title ?? selectedName}
            </Text>
          )}
          {hideDownArrow ? null : (
            <Icon
              type="font-awesome-5"
              name="caret-down"
              size={13}
              color={theme.colors?.primary}
              solid
            />
          )}
        </View>
      </MenuTrigger>
      <MenuOptions>
        <SectionList
          style={[
            styles.sectionList,
            { maxHeight: dimensions.height - 200 },
            showCustomFilters && activeFilterNames
              ? styles.scheduleFilterSectionList
              : {},
          ]}
          contentContainerStyle={styles.sectionListContentContainer}
          renderSectionHeader={({ section }) =>
            section.title && section.data.length > 0 ? (
              <Text style={styles.sectionHeader}>{section.title}</Text>
            ) : null
          }
          sections={sections}
          extraData={selectedId}
          ListFooterComponent={
            dropdownFooterOptionsProps ? (
              <DropdownFooterOptions {...dropdownFooterOptionsProps} />
            ) : null
          }
          renderItem={({
            item,
          }: SectionListRenderItemInfo<Item, DropdownSection<Item>>) => (
            <View>
              {isScheduleSearchOption(item) ? (
                <Divider style={styles.divider} />
              ) : null}
              <MenuOption
                style={{ padding: 1 }}
                key={item.id}
                onSelect={() => onSelect(item.id)}
              >
                {ItemComponent ? (
                  <ItemComponent
                    item={item}
                    selected={item.id === selectedId}
                  />
                ) : (
                  <View style={styles.item}>
                    <Text
                      style={[
                        styles.itemText,
                        isScheduleSearchOption(item) ? styles.searchText : {},
                      ]}
                    >
                      {item.name}
                    </Text>
                    <View style={styles.itemIconContainer}>
                      {!isScheduleSearchOption(item) &&
                      item.id === selectedId ? (
                        <CheckIcon />
                      ) : null}
                    </View>
                  </View>
                )}
              </MenuOption>
              {isScheduleSearchOption(item) &&
              item.id === selectedId &&
              filterNames ? (
                <ActiveFiltersOption filterNames={filterNames} />
              ) : null}
            </View>
          )}
        />
      </MenuOptions>
    </Menu>
  )
}

interface ActiveFiltersOptionProps {
  filterNames: string
}

function ActiveFiltersOption({ filterNames }: ActiveFiltersOptionProps) {
  const styles = useStyles()
  return (
    <View style={[styles.item, styles.activeFiltersItem]}>
      <Text
        style={[styles.itemText, styles.activeFilterstext]}
        numberOfLines={1}
      >
        {filterNames}
      </Text>
      <View style={styles.itemIconContainer}>
        <CheckIcon />
      </View>
    </View>
  )
}

function DropdownFooterOptions({
  showSummaryTilesOptionProps,
  showWeeklySummaryOptionProps,
  showEventsOptionProps,
}: DropdownFooterOptionsProps) {
  const styles = useStyles()
  if (
    !showSummaryTilesOptionProps.showSummaryTilesOption &&
    !showWeeklySummaryOptionProps.showWeeklySummaryOption &&
    !showEventsOptionProps.showEventsTilesOption
  ) {
    return null
  }
  return (
    <View>
      {showEventsOptionProps.showEventsTilesOption ? (
        <>
          <Divider style={styles.divider} />
          <ShowEventsOption {...showEventsOptionProps} />
        </>
      ) : (
        <></>
      )}
      <Divider style={styles.divider} />
      <ShowWeeklySummaryOption {...showWeeklySummaryOptionProps} />
      <ShowSummaryTilesOption {...showSummaryTilesOptionProps} />
    </View>
  )
}

function CheckIcon({}) {
  const { theme } = useTheme()
  return (
    <Icon
      type="font-awesome-5"
      name="check"
      size={15}
      color={theme.colors?.primary}
      solid
    />
  )
}

export interface ShowEventsOptionProps {
  showEvents?: ShowEventsOptions
  setShowEvents: React.Dispatch<React.SetStateAction<ShowEventsOptions>>
  showEventsTilesOption?: boolean
}

function ShowEventsOption({
  setShowEvents,
  showEvents,
  showEventsTilesOption,
}: ShowEventsOptionProps) {
  const styles = useStyles()

  return showEventsTilesOption ? (
    <MenuOptions>
      <MenuOption
        onSelect={() =>
          setShowEvents({
            holidays: !showEvents?.holidays,
            timeOff: showEvents?.timeOff ?? true,
            customEvents: showEvents?.customEvents ?? true,
            tasks: showEvents?.tasks ?? true,
          })
        }
      >
        <View style={styles.item}>
          <Text style={styles.itemText}>{t('holidays')}</Text>
          <View style={styles.itemIconContainer}>
            {showEvents?.holidays ? <CheckIcon /> : null}
          </View>
        </View>
      </MenuOption>
      <MenuOption
        onSelect={() =>
          setShowEvents({
            holidays: showEvents?.holidays ?? true,
            timeOff: !showEvents?.timeOff,
            customEvents: showEvents?.customEvents ?? true,
            tasks: showEvents?.tasks ?? true,
          })
        }
      >
        <View style={styles.item}>
          <Text style={styles.itemText}>{t('timeOff')}</Text>
          <View style={styles.itemIconContainer}>
            {showEvents?.timeOff ? <CheckIcon /> : null}
          </View>
        </View>
      </MenuOption>
      <MenuOption
        onSelect={() =>
          setShowEvents({
            holidays: showEvents?.holidays ?? true,
            timeOff: showEvents?.timeOff ?? true,
            customEvents: !showEvents?.customEvents,
            tasks: showEvents?.tasks ?? true,
          })
        }
      >
        <View style={styles.item}>
          <Text style={styles.itemText}>{t('customEvent')}</Text>
          <View style={styles.itemIconContainer}>
            {showEvents?.customEvents ? <CheckIcon /> : null}
          </View>
        </View>
      </MenuOption>
      <MenuOption
        onSelect={() =>
          setShowEvents({
            holidays: showEvents?.holidays ?? true,
            timeOff: showEvents?.timeOff ?? true,
            customEvents: showEvents?.customEvents ?? true,
            tasks: !showEvents?.tasks,
          })
        }
      >
        <View style={styles.item}>
          <Text style={styles.itemText}>{t('tasks')}</Text>
          <View style={styles.itemIconContainer}>
            {showEvents?.tasks ? <CheckIcon /> : null}
          </View>
        </View>
      </MenuOption>
    </MenuOptions>
  ) : null
}

export interface ShowSummaryTilesOptionProps {
  showSummaryTiles?: boolean
  setShowSummaryTiles: React.Dispatch<React.SetStateAction<boolean | undefined>>
  showSummaryTilesOption?: boolean
}

function ShowSummaryTilesOption({
  setShowSummaryTiles,
  showSummaryTiles,
  showSummaryTilesOption,
}: ShowSummaryTilesOptionProps) {
  const styles = useStyles()

  return showSummaryTilesOption ? (
    <MenuOption onSelect={() => setShowSummaryTiles(!showSummaryTiles)}>
      <View style={styles.item}>
        <Text style={styles.itemText}>{t('showSummaryTiles')}</Text>
        <View style={styles.itemIconContainer}>
          {showSummaryTiles ? <CheckIcon /> : null}
        </View>
      </View>
    </MenuOption>
  ) : null
}

export interface ShowWeeklySummaryOptionProps {
  showWeeklySummary: boolean
  setShowWeeklySummary: React.Dispatch<React.SetStateAction<boolean>>
  showWeeklySummaryOption?: boolean
}

function ShowWeeklySummaryOption({
  showWeeklySummary,
  setShowWeeklySummary,
  showWeeklySummaryOption,
}: ShowWeeklySummaryOptionProps) {
  const styles = useStyles()
  return showWeeklySummaryOption ? (
    <MenuOption onSelect={() => setShowWeeklySummary(!showWeeklySummary)}>
      <View style={styles.item}>
        <Text style={styles.itemText}>{t('showWeeklySummary')}</Text>
        <View style={styles.itemIconContainer}>
          {showWeeklySummary ? <CheckIcon /> : null}
        </View>
      </View>
    </MenuOption>
  ) : null
}

const useStyles = makeStyles(theme => ({
  sectionList: {
    paddingTop: 10,
    paddingLeft: 10,
    backgroundColor: theme.colors?.white,
  },
  sectionHeader: {
    marginTop: 10,
    marginBottom: 2,
    fontFamily: font.bold,
  },
  anchorStyle: {
    backgroundColor: theme.colors?.white,
  },
  itemText: {
    fontSize: 13,
  },
  itemIconContainer: {
    width: 25,
    marginBottom: 2,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  buttonText: {
    fontSize: 17,
    fontFamily: font.bold,
    textAlign: 'center',
    marginRight: 2,
    color: theme.colors?.primary,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  divider: {
    backgroundColor: theme.colors?.grey2,
    fontWeight: 'bold',
    height: 1,
    marginVertical: 6,
    marginRight: 14,
    marginLeft: 4,
  },
  searchText: {
    fontStyle: 'italic',
  },
  scheduleFilterSectionList: {
    width: 245,
  },
  activeFiltersItem: { marginTop: 8, marginLeft: 6, flex: 1 },
  activeFilterstext: { flexShrink: 1 },
  icon: {
    paddingVertical: 10,
    paddingHorizontal: 15,
  },
  sectionListContentContainer: { paddingBottom: 20 },
}))
